import Component from '@glimmer/component'
import { tracked } from '@glimmer/tracking'
import { action } from '@ember/object'
import { service } from '@ember/service'
import { fn, uniqueId } from '@ember/helper'
import { on } from '@ember/modifier'
import { t } from 'ember-intl'
import { startOfDay, endOfDay, addDays, format } from 'date-fns'

import config from 'teacher-ui-client/config/environment'

import type { SubscriptionType } from 'teacher-ui-client/subscription-type/subscription-types'
import type Teacher from 'teacher-ui-client/models/teacher'
import type Student from 'teacher-ui-client/models/student'
import type { IntlService } from 'ember-intl'
import type SessionService from 'teacher-ui-client/services/session'
import type AuthToken from '@blakeelearning/auth/services/auth-token'
import type { Log } from '@blakeelearning/log'
import type RouterService from '@ember/routing/router-service'
import { SectionBox } from 'teacher-ui-client/components/assignments-admin/section-box'
import { TeacherSelect } from 'teacher-ui-client/components/base/select/teacher'
import { SelectStudentsTable } from 'teacher-ui-client/components/assignments-admin/select-students-table'
import { themeKey } from 'teacher-ui-client/helpers/theme-key'
import { BaseSelect } from 'teacher-ui-client/components/base/select'
import { eventValue } from 'teacher-ui-client/helpers/event-value'
import { eq, or } from 'ember-truth-helpers'
import { AssignmentsAdminDateRange } from 'teacher-ui-client/components/assignments-admin/date-range'
import { ActivitySelectorLessonsByGradeList } from 'teacher-ui-client/components/assignments-admin/activity-selector/lessons-by-grade-list'
import { ActivitySelectorLessonsByStageList } from 'teacher-ui-client/components/assignments-admin/activity-selector/lessons-by-stage-list'
import { ActivitySelectorDrivingTestsList } from 'teacher-ui-client/components/assignments-admin/activity-selector/driving-tests-list'
import { ActivitySelectorBookList } from 'teacher-ui-client/components/assignments-admin/activity-selector/book-list'
import { ActivitySelectorEWIList } from 'teacher-ui-client/components/assignments-admin/activity-selector/ewi-list'
import { ActivitySelectorLongWriteList } from 'teacher-ui-client/components/assignments-admin/activity-selector/long-write-list'
import { ActivitySelectorLessonList } from 'teacher-ui-client/components/assignments-admin/activity-selector/lesson-list'
import { AssignmentDetails } from 'teacher-ui-client/components/assignments-admin/assignment-details'
import { AssignmentCreatedModal } from 'teacher-ui-client/components/assignments-admin/assignment-modal'
import type { AssignmentPrecinct, AssignmentType } from 'teacher-ui-client/models/assignment'
import { Precinct } from 'teacher-ui-client/utils/precincts'
import type BookLibraryBook from 'teacher-ui-client/models/book-library/book'
import { queryParam } from 'teacher-ui-client/decorators/query-param'
import { OnChange } from 'teacher-ui-client/modifiers/on-change'
import { UiButtonLinkTo } from 'teacher-ui-client/components/ui-button/link-to'
import { join } from 'teacher-ui-client/utils/uri'
import type Owner from '@ember/owner'
import PhosphorIcon from 'teacher-ui-client/components/phosphor-icon'
import type TeacherEvents from 'teacher-ui-client/services/teacher-events'

interface Signature {
  Element: HTMLDivElement
  Args: {
    subscriptionType: SubscriptionType
    teachers: Teacher[]
    students: Student[]
    assignmentProduct: LegacyProductRe | LegacyProductRex | LegacyProductMs | LegacyProductWl | LegacyProductFp
  }
}

export const ActivityType = {
  ReReadingLesson: Precinct.RE_READING,
  ReSpellingLesson: Precinct.RE_SPELLING,
  ReStorylandsLesson: Precinct.RE_STORYLANDS,
  ReDrivingTestLesson: Precinct.RE_DRIVING_TESTS,
  RexReadingLesson: Precinct.REX_READING,
  RexSpellingLesson: Precinct.REX_SPELLING,
  RexLibraryQuiz: `${Precinct.REX_LIBRARY}-quiz`,
  RexLibraryReadAloud: `${Precinct.REX_LIBRARY}-read-aloud`,
  FpLesson: Precinct.FP_PEAKS,
  MsLesson: Precinct.MS_LESSONS,
  MsDrivingTestLesson: Precinct.MS_DRIVING_TESTS,
  WlLesson: Precinct.WL_WRITING,
  WlExcitingWritingIdeas: Precinct.WL_EXCITING_WRITING_IDEAS,
  WlLongWrites: Precinct.WL_LONG_WRITES,
} as const

export type ActivityType = ValuesOf<typeof ActivityType>

/**
 * Indicates some attributes about the type of activity the user would like to do. Most importantly the precinct, but
 * this in itself is not an identifier of the option, as one precinct might have multiple "activity types"
 *
 * For example library precinct has 4 assignment types (book, chapter, read_aloud_book, read_aloud_chapter) - these are
 * loosely split into 2 activity type options (read aloud vs not read aloud)
 */
interface ActivityTypeOption {
  id: ActivityType
  title: string
  precinct: Precinct
  assignmentPrecinct: AssignmentPrecinct
}

export interface AssignmentOption {
  id: string // id for local option comparison is string
  label: string
  gradePosition?: number
  book?: BookLibraryBook
  /**
   * If this option is selected, this hash will be dumped into the create assignment request. It will be different
   * depending on the chosen activity
   */
  assignmentData: {
    assignment_id: string
    assignment_type: AssignmentType
    chapters?: number[]
    description?: string // description for fast phonics peaks
    [other: string]: unknown
  }
}

export interface AssignmentResponse {
  assignment_uuid?: string
}

export class AssignmentForm extends Component<Signature> {
  @service authToken!: AuthToken
  @service intl!: IntlService
  @service log!: Log
  @service router!: RouterService
  @service session!: SessionService
  @service teacherEvents!: TeacherEvents

  constructor(owner: Owner, args: Signature['Args']) {
    super(owner, args)
    this.assignmentTeacherId = this.session.currentTeacher.id

    this.resetAssignmentDates()
  }

  @tracked assignmentStudentIds: string[] = []
  /**
   * Who the assignment is attached to (subco can create assignments on behalf of other teachers)
   */
  @tracked assignmentTeacherId: string

  @tracked assignmentStartAt: Date = new Date()

  @tracked assignmentEndAt: Date = new Date()

  @queryParam('assignments/product/create') activityType?: ActivityType

  get selectedActivityTypeOption(): ActivityTypeOption | undefined {
    return this.activityTypeOptionsForProduct.find((option) => option.id === this.activityType)
  }

  @tracked selectedAssignmentOption?: AssignmentOption

  @tracked isShowingModal = false

  get selectedStudents() {
    return this.assignmentStudentIds.reduce<Student[]>((selectedStudents, studentId) => {
      const found = this.args.students.find((student) => student.id === studentId)
      return found ? [...selectedStudents, found] : selectedStudents
    }, [])
  }

  get currentUserIsSubco() {
    return this.session.currentTeacher.subcoFor(this.args.subscriptionType)
  }

  get activityTypeOptionsForProduct(): ActivityTypeOption[] {
    const lesson = this.intl.t('assignments.create.activityLesson')
    const readingLesson = this.intl.t('assignments.create.activityReadingLesson')
    const comprehensionLesson = this.intl.t('assignments.create.activityComprehensionLesson')
    const spellingLesson = this.intl.t('assignments.create.activitySpellingLesson')
    const storylandsLesson = this.intl.t('assignments.create.activityStorylandsLesson')
    const drivingTestsLesson = this.intl.t('assignments.create.activityDrivingTestsLesson')
    const libraryBook = this.intl.t('assignments.create.activityLibraryBook')
    const readAloudBook = this.intl.t('assignments.create.activityReadAloudBook')
    const ewi = this.intl.t('assignments.create.activityEwi')
    const longWrite = this.intl.t('assignments.create.activityLongWrite')
    const fastPhonicsPeak = this.intl.t('assignments.create.activityFastPhonicsPeak')

    switch (this.args.assignmentProduct) {
      case 'maths':
        return [
          { id: ActivityType.MsLesson, precinct: Precinct.MS_LESSONS, assignmentPrecinct: 'my_lessons', title: lesson },
          {
            id: ActivityType.MsDrivingTestLesson,
            precinct: Precinct.MS_DRIVING_TESTS,
            assignmentPrecinct: 'driving_tests',
            title: drivingTestsLesson,
          },
        ]
      case 'writing_legends': {
        const wlPrecincts: ActivityTypeOption[] = [
          { id: ActivityType.WlLesson, precinct: Precinct.WL_WRITING, assignmentPrecinct: 'lessons', title: lesson },
          {
            id: ActivityType.WlExcitingWritingIdeas,
            precinct: Precinct.WL_EXCITING_WRITING_IDEAS,
            assignmentPrecinct: 'ewi',
            title: ewi,
          },
          {
            id: ActivityType.WlLongWrites,
            precinct: Precinct.WL_LONG_WRITES,
            assignmentPrecinct: 'long-writes',
            title: longWrite,
          },
        ]

        return wlPrecincts
      }
      case 'rex':
        return [
          {
            id: ActivityType.RexReadingLesson,
            precinct: Precinct.REX_READING,
            assignmentPrecinct: 'my_lessons',
            title: comprehensionLesson,
          },
          {
            id: ActivityType.RexSpellingLesson,
            precinct: Precinct.REX_SPELLING,
            assignmentPrecinct: 'english_skills_spelling',
            title: spellingLesson,
          },
          {
            id: ActivityType.RexLibraryQuiz,
            precinct: Precinct.REX_LIBRARY,
            assignmentPrecinct: 'library',
            title: libraryBook,
          },
          {
            id: ActivityType.RexLibraryReadAloud,
            precinct: Precinct.REX_LIBRARY,
            assignmentPrecinct: 'library',
            title: readAloudBook,
          },
        ]
      case 're':
        return [
          {
            id: ActivityType.ReReadingLesson,
            precinct: Precinct.RE_READING,
            assignmentPrecinct: 'lessons',
            title: readingLesson,
          },
          {
            id: ActivityType.ReSpellingLesson,
            precinct: Precinct.RE_SPELLING,
            assignmentPrecinct: 'spelling',
            title: spellingLesson,
          },
          {
            id: ActivityType.ReStorylandsLesson,
            precinct: Precinct.RE_STORYLANDS,
            assignmentPrecinct: 'clinker_castle',
            title: storylandsLesson,
          },
          {
            id: ActivityType.ReDrivingTestLesson,
            precinct: Precinct.RE_DRIVING_TESTS,
            assignmentPrecinct: 'driving_tests',
            title: drivingTestsLesson,
          },
        ]
      case 'fast_phonics':
        return [
          {
            id: ActivityType.FpLesson,
            precinct: Precinct.FP_PEAKS,
            assignmentPrecinct: 'lessons',
            title: fastPhonicsPeak,
          },
        ]
      default:
        return []
    }
  }

  get teachersForSelect() {
    return [...this.args.teachers].sort((a, b) => a.fullName.localeCompare(b.fullName))
  }

  get isLibraryType() {
    return this.selectedActivityTypeOption?.precinct === Precinct.REX_LIBRARY
  }

  get isLongWriteType() {
    return this.selectedActivityTypeOption?.precinct === Precinct.WL_LONG_WRITES
  }

  get isExcitingWritingIdeasType() {
    return this.selectedActivityTypeOption?.precinct === Precinct.WL_EXCITING_WRITING_IDEAS
  }

  @action
  resetAssignmentDates() {
    // Operate with day values. On saving, we'll use these values to determine the desired start/due dates.
    const today = startOfDay(new Date())
    const tomorrow = startOfDay(addDays(today, 1))
    this.assignmentStartAt = today
    this.assignmentEndAt = tomorrow
  }

  @action
  updateSelectedActivityType(optionId: ActivityType | '') {
    // ensure empty string (falsey) placeholder value is saved as undefined
    this.activityType = optionId ? optionId : undefined
    this.selectedAssignmentOption = undefined
  }

  @action
  resetSelectedActivityType() {
    this.activityType = undefined
    this.selectedAssignmentOption = undefined
  }

  @action
  resetSelectedAssignmentOption() {
    this.selectedAssignmentOption = undefined
  }

  /**
   * Returns the activity text for the assignments details activity section & create modal
   */
  get activityText() {
    if (!this.selectedActivityTypeOption || !this.selectedAssignmentOption) return ''

    let activityTypeLabel = this.intl.t('assignments.create.activityType.lesson')

    if (this.selectedActivityTypeOption.id === ActivityType.FpLesson) {
      activityTypeLabel = this.intl.t('assignments.create.activityType.peak')
    }

    if (this.selectedActivityTypeOption.id === ActivityType.RexLibraryReadAloud) {
      activityTypeLabel = this.intl.t('assignments.create.activityType.readAloud')
    }

    if (this.selectedActivityTypeOption.id === ActivityType.RexLibraryQuiz) {
      activityTypeLabel = this.intl.t('assignments.create.activityType.libraryQuiz')
    }

    if (this.selectedActivityTypeOption.id === ActivityType.WlExcitingWritingIdeas) {
      activityTypeLabel = this.intl.t('assignments.create.activityType.ewi')
    }

    if (this.selectedActivityTypeOption.id === ActivityType.WlLongWrites) {
      activityTypeLabel = this.intl.t('assignments.create.activityType.longWrite')
    }

    return `${activityTypeLabel} - ${this.selectedAssignmentOption.label}`
  }

  /**
   * generalFormData returns an object which ready to be send to any of our assignments apis as a part.
   * It contains all commonly used properties which overlap between re/rex/ms
   */
  get generalFormData() {
    const studentIds = this.assignmentStudentIds.map((id) => parseInt(id, 10))
    /**
     * The dates chosen should be the start of the selected days.
     * When submitting, adjust the due date to be the end of the selected day.
     * This is because if teacher chose a due date of 10th March, they would not expect 00:00am 10th March, but rather 11:59pm 10th March.
     */
    const startAt = startOfDay(this.assignmentStartAt)
    const endAt = endOfDay(this.assignmentEndAt)

    return {
      product: this.args.assignmentProduct,
      precinct: this.selectedActivityTypeOption?.assignmentPrecinct,
      student_ids: studentIds,
      start_at: format(startAt, 'yyyy-MM-dd'),
      end_at: format(endAt, 'yyyy-MM-dd'),
      teacher_id: parseInt(this.assignmentTeacherId, 10),
      accent: this.session.currentSchool?.accent?.extension,
    }
  }

  @action
  selectAssignmentAction(assignmentOption?: AssignmentOption) {
    this.selectedAssignmentOption = assignmentOption
  }

  @action
  updateDates({ startDate, endDate }: { startDate: Date; endDate?: Date | null }) {
    if (endDate) {
      this.assignmentStartAt = startDate
      this.assignmentEndAt = endDate
    }
  }

  @action
  updateSelectedStudentIds(studentIds: string[]) {
    this.assignmentStudentIds = studentIds
  }

  @action
  updateAssignmentTeacherId(teacherId: string) {
    this.assignmentStudentIds = []
    this.assignmentTeacherId = teacherId
  }

  /**
   * Closes the assignment confirmation modal, resets the selected activity
   * and scrolls the page up to the top
   */
  @action
  closeModal() {
    this.isShowingModal = false
    this.resetSelectedActivityType()
    window.scrollTo(0, 0)
  }

  /**
   * Resets the selected students back to the default.
   */
  @action
  resetStudents() {
    this.assignmentStudentIds = []
  }

  /**
   * Collects the assignment data from the components sends a request to save the assignment
   *
   * ```
   * assignment = {
   *   product: 'maths',
   *   precinct: 'my_lessons',
   *   assignment_type: 'lesson',
   *   assignment_id: '12',
   *   student_ids: [2, 3, 4],
   *   start_at: '2016-11-04',
   *   end_at: '2016-12-01',
   *   teacher_id: 241124,
   * }
   * ```
   */
  @action
  async submitAssignment() {
    const assignment = { ...this.generalFormData, ...this.selectedAssignmentOption?.assignmentData }

    const url = join(config.gravityV3Url, 'assignments', 'teacher', assignment.product)

    const response = await fetch(url, {
      method: 'POST',
      body: JSON.stringify({ assignment }),
      headers: {
        Authorization: this.authToken.token ?? '',
        'content-type': 'application/json',
      },
    })
    if (!response.ok) {
      const errorText = await response.text()
      this.log.error('post assignment failed', errorText, assignment)
      return
    }

    this.isShowingModal = true

    const json = (await response.json()) as AssignmentResponse

    this.teacherEvents.sendEvent('assignment_created', this.generalFormData.product, {
      accent: this.generalFormData.accent,
      assignmentEnd: assignment.end_at,
      assignmentId: json.assignment_uuid,
      assignmentStart: assignment.start_at,
      lessonData: this.selectedAssignmentOption?.assignmentData,
      precinct: this.generalFormData.precinct,
      studentIds: assignment.student_ids,
      teacherId: this.generalFormData.teacher_id,
    })
  }

  @action
  transitionToManage() {
    void this.router.transitionTo('assignments.product.manage.current', { queryParams: { page: 1 } })
  }

  get productColor() {
    switch (this.args.assignmentProduct) {
      case 'maths':
        return 'bg-ms-green-100'
      case 're':
      case 'rex':
        return 'bg-oceany-blue-100'
      case 'writing_legends':
        return 'bg-wl-blue-100'
      default:
        return ''
    }
  }

  <template>
    <div class="block w-full" {{OnChange this.resetSelectedAssignmentOption @assignmentProduct}} ...attributes>
      <div class="inline-block space-y-7 px-4 md:mb-8 md:w-full lg:w-2/3">
        {{! BEGIN TEACHER SELECTION SECTION }}
        {{#if this.currentUserIsSubco}}
          <SectionBox
            class="section-box-teacher"
            @label={{t "assignments.form.chooseTeacherSectionLabel"}}
            @icon="phosphor-user-fill"
            @resetAction={{fn this.updateAssignmentTeacherId this.session.currentTeacher.id}}
            @appearance={{@assignmentProduct}}
          >
            <div class="space-y-1 px-11 py-8">
              <label class="block">
                {{t "assignments.form.chooseTeacherSectionLabel"}}
              </label>
              <TeacherSelect
                @teachers={{this.teachersForSelect}}
                @teacherId={{this.assignmentTeacherId}}
                @updateTeacherId={{this.updateAssignmentTeacherId}}
                @themeKey={{themeKey @subscriptionType}}
              />
            </div>
          </SectionBox>
        {{/if}}
        {{! END TEACHER SELECTION SECTION }}

        {{! BEGIN STUDENT SELECTION SECTION }}
        <SectionBox
          class="overflow-hidden {{this.productColor}}"
          @label={{t "assignments.form.selectStudentsSectionLabel"}}
          @icon="phosphor-user-fill"
          @resetAction={{this.resetStudents}}
          @appearance={{@assignmentProduct}}
        >
          <SelectStudentsTable
            @subscriptionType={{@subscriptionType}}
            @assignmentTeacherId={{this.assignmentTeacherId}}
            @selectedStudentIds={{this.assignmentStudentIds}}
            @updateSelectedStudentIds={{this.updateSelectedStudentIds}}
          />
        </SectionBox>
        {{! END STUDENT SELECTION SECTION }}

        {{! BEGIN DATES SELECTION SECTION }}
        <SectionBox
          class="section-box-dates"
          @label={{t "assignments.form.addStartAndFinishDateSectionLabel"}}
          @icon="phosphor-bell-fill"
          @resetAction={{this.resetAssignmentDates}}
          @appearance={{@assignmentProduct}}
        >
          <div class="p-8">
            <AssignmentsAdminDateRange
              class="mx-3"
              @startDate={{this.assignmentStartAt}}
              @endDate={{this.assignmentEndAt}}
              @onDateSelect={{this.updateDates}}
              @themeKey={{themeKey @subscriptionType}}
            />
          </div>
        </SectionBox>
        {{! END DATES SELECTION SECTION }}

        {{! BEGIN LESSON & ACTIVITY SELECTION SECTION }}
        <SectionBox
          class="section-box-activity"
          @label={{t "assignments.form.chooseAnActivityPlaceholder"}}
          @icon="phosphor-list-fill"
          @resetAction={{this.resetSelectedActivityType}}
          @appearance={{@assignmentProduct}}
        >
          <div class="p-8">
            <div class="mb-4 px-3 md:w-full lg:w-1/2 lg:min-w-fit">
              {{#let (uniqueId) as |uniqId|}}
                <label id="{{uniqId}}-assignment-activity" class="mb-1">
                  {{t "assignments.form.typeOfActivityLabel"}}
                </label>
                <div class="flex gap-4">
                  <BaseSelect
                    data-test-select-activity-type
                    name="activity-type"
                    class="w-full"
                    value={{or this.selectedActivityTypeOption.id ""}}
                    aria-labelledby="{{uniqId}}-assignment-activity"
                    @themeKey={{themeKey @subscriptionType}}
                    {{on "input" (eventValue this.updateSelectedActivityType)}}
                  >
                    <option value="">{{t "assignments.create.activityPlaceholder"}}</option>
                    {{#each this.activityTypeOptionsForProduct as |optionItem|}}
                      <option value={{optionItem.id}} selected={{eq optionItem.id this.selectedActivityTypeOption.id}}>
                        {{optionItem.title}}
                      </option>
                    {{/each}}
                  </BaseSelect>
                  {{#if this.isLibraryType}}
                    <UiButtonLinkTo
                      @route="subscription-type.book-library.search"
                      class="whitespace-nowrap"
                      target="_blank"
                    >
                      {{t "assignments.form.viewTheLibrary"}}<PhosphorIcon
                        @icon="phosphor-arrow-square-out-bold"
                        class="ml-2"
                      />
                    </UiButtonLinkTo>
                  {{/if}}

                  {{#if this.isLongWriteType}}
                    <UiButtonLinkTo @route="subscription-type.long-writes" class="whitespace-nowrap" target="_blank">
                      {{t "assignments.form.viewLongWrites"}}<PhosphorIcon
                        @icon="phosphor-arrow-square-out-bold"
                        class="ml-2"
                      />
                    </UiButtonLinkTo>
                  {{/if}}

                  {{#if this.isExcitingWritingIdeasType}}
                    <UiButtonLinkTo @route="subscription-type.writing-ideas" class="whitespace-nowrap" target="_blank">
                      {{t "assignments.form.viewEwis"}}<PhosphorIcon
                        @icon="phosphor-arrow-square-out-bold"
                        class="ml-2"
                      />
                    </UiButtonLinkTo>
                  {{/if}}
                </div>
              {{/let}}
            </div>
            {{! SELECT ACTIVITY }}
            {{#if this.selectedActivityTypeOption}}
              {{#if
                (or
                  (eq this.selectedActivityTypeOption.precinct Precinct.RE_READING)
                  (eq this.selectedActivityTypeOption.precinct Precinct.RE_SPELLING)
                  (eq this.selectedActivityTypeOption.precinct Precinct.RE_STORYLANDS)
                  (eq this.selectedActivityTypeOption.precinct Precinct.REX_READING)
                  (eq this.selectedActivityTypeOption.precinct Precinct.MS_LESSONS)
                )
              }}
                <ActivitySelectorLessonsByGradeList
                  @precinct={{this.selectedActivityTypeOption.precinct}}
                  @selectAssignmentAction={{this.selectAssignmentAction}}
                  @selectedAssignmentOption={{this.selectedAssignmentOption}}
                />
              {{else if (eq this.selectedActivityTypeOption.precinct Precinct.REX_SPELLING)}}
                <ActivitySelectorLessonsByStageList
                  @precinct={{this.selectedActivityTypeOption.precinct}}
                  @selectAssignmentAction={{this.selectAssignmentAction}}
                  @selectedAssignmentOption={{this.selectedAssignmentOption}}
                />
              {{else if
                (or
                  (eq this.selectedActivityTypeOption.precinct Precinct.RE_DRIVING_TESTS)
                  (eq this.selectedActivityTypeOption.precinct Precinct.MS_DRIVING_TESTS)
                )
              }}
                <ActivitySelectorDrivingTestsList
                  @precinct={{this.selectedActivityTypeOption.precinct}}
                  @selectAssignmentAction={{this.selectAssignmentAction}}
                  @selectedAssignmentOption={{this.selectedAssignmentOption}}
                />
              {{else if (eq this.selectedActivityTypeOption.precinct Precinct.REX_LIBRARY)}}
                <ActivitySelectorBookList
                  @isReadAloud={{eq this.selectedActivityTypeOption.id ActivityType.RexLibraryReadAloud}}
                  @selectAssignmentAction={{this.selectAssignmentAction}}
                  @selectedAssignmentOption={{this.selectedAssignmentOption}}
                />
              {{else if
                (or
                  (eq this.selectedActivityTypeOption.precinct Precinct.WL_WRITING)
                  (eq this.selectedActivityTypeOption.precinct Precinct.FP_PEAKS)
                )
              }}

                <ActivitySelectorLessonList
                  @selectAssignmentAction={{this.selectAssignmentAction}}
                  @precinct={{this.selectedActivityTypeOption.precinct}}
                  @selectedAssignmentOption={{this.selectedAssignmentOption}}
                />
              {{else if (eq this.selectedActivityTypeOption.precinct Precinct.WL_EXCITING_WRITING_IDEAS)}}
                <ActivitySelectorEWIList
                  @selectAssignmentAction={{this.selectAssignmentAction}}
                  @selectedAssignmentOption={{this.selectedAssignmentOption}}
                />
              {{else if (eq this.selectedActivityTypeOption.precinct Precinct.WL_LONG_WRITES)}}
                <ActivitySelectorLongWriteList
                  @selectAssignmentAction={{this.selectAssignmentAction}}
                  @selectedAssignmentOption={{this.selectedAssignmentOption}}
                />
              {{/if}}
            {{/if}}
          </div>
        </SectionBox>
        {{! END LESSON & ACTIVITY SELECTION SECTION }}
      </div>
      {{! BEGIN ASSIGNMENTS DETAILS (RHS) SECTION }}
      <div class="sticky top-8 inline-block px-4 md:w-full lg:float-right lg:w-1/3">
        <AssignmentDetails
          @assignmentProduct={{@assignmentProduct}}
          @assignmentPrecinct={{this.selectedActivityTypeOption.assignmentPrecinct}}
          @students={{this.selectedStudents}}
          @startAt={{this.assignmentStartAt}}
          @endAt={{this.assignmentEndAt}}
          @selectedAssignmentOption={{this.selectedAssignmentOption}}
          @activityText={{this.activityText}}
          @submitAction={{this.submitAssignment}}
        />
      </div>
      {{! END ASSIGNMENTS DETAILS (RHS) SECTION }}
    </div>

    <AssignmentCreatedModal
      @themeKey={{themeKey @subscriptionType}}
      @assignmentDescription={{this.activityText}}
      @studentCount={{this.assignmentStudentIds.length}}
      @startAt={{this.assignmentStartAt}}
      @showModal={{this.isShowingModal}}
      @closeModalAction={{this.closeModal}}
      @confirmAction={{this.transitionToManage}}
      @rejectAction={{this.closeModal}}
    />
  </template>
}
